"use client";

import { type ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@punchup/utils";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../shadcn/ui/dialog";
import { PunchupButton } from "./PunchupButton";

export interface BetaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  itemsHeading?: string;
  items?: { title: string; description?: string; icon?: ReactNode }[];
  footer?: ReactNode;
  dismissLabel?: string;
  className?: string;
  headerClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  itemsClassName?: string;
  buttonClassName?: string;
  footerClassName?: string;
}

export function BetaModal({
  open,
  onOpenChange,
  title,
  description,
  itemsHeading,
  items,
  footer,
  dismissLabel = "Got it",
  className,
  headerClassName,
  titleClassName,
  descriptionClassName,
  itemsClassName,
  buttonClassName,
  footerClassName,
}: BetaModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("p-0 gap-0", className)}>
        <div className="pt-10 pb-10 px-8">
          <DialogHeader
            className={cn(
              "space-y-0 text-center sm:text-center",
              headerClassName
            )}
          >
            <DialogTitle
              className={cn(
                "text-[22px] font-bold leading-tight tracking-normal",
                titleClassName
              )}
            >
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription
                className={cn("text-base font-normal", descriptionClassName)}
              >
                {description}
              </DialogDescription>
            )}
          </DialogHeader>

          {items && items.length > 0 && (
            <div className={cn("mt-9 flex flex-col gap-3", itemsClassName)}>
              {itemsHeading && (
                <p className="text-sm font-medium">{itemsHeading}</p>
              )}
              <ul className="flex flex-col gap-6">
                {items.map((item) => (
                  <li key={item.title} className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0 text-primary">
                      {item.icon ?? <Check className="h-5 w-5" />}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-base font-semibold">
                        {item.title}
                      </span>
                      {item.description && (
                        <span className="text-sm text-muted-foreground">
                          {item.description}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <PunchupButton
            className={cn("mt-9 w-full", buttonClassName)}
            onClick={() => onOpenChange(false)}
          >
            {dismissLabel}
          </PunchupButton>

          {footer && (
            <div
              className={cn(
                "mt-3 text-center text-xs text-muted-foreground",
                footerClassName
              )}
            >
              {footer}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
